import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Scale,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Database,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import styles from '../../../styles/MasterView.module.css';
import api from '../../../api/axios';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import UnitDrawer from './UnitDrawer';
import NotFound from '../../../pages/Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewUnit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchUnit = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/units/${id}`);
      if (response.data.success) {
        setUnit(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching unit details:', error);
      toast.error('Failed to load unit details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnit();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Unit?',
      text: `Are you sure you want to delete ${unit.unit_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/units/${unit.id}`);
          if (response.data.success) {
            toast.success('Unit deleted successfully');
            navigate('/units');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting unit');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Unit Profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Unit Details</h1>
              <p>View technical specifications and system-wide usage metrics</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/units')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Unit</span>
              </button>
              <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444' }}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <Scale size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{unit.unit_name}</h1>
                  <span className={styles['status-badge']}>
                    <ShieldCheck size={14} /> ACTIVE UNIT
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Database size={16} />
                    <span>Classification: <strong>Measurement Unit</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Registered: <strong>{new Date(unit.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Database size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>System Usage</label>
                <h2>124</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><CheckCircle2 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Inventory Links</label>
                <h2>42</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Activity Rate</label>
                <h2>High</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Validation</label>
                <h2>Verified</h2>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Primary Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Database size={20} /></div>
                  <h3>Unit Configuration</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Full Descriptor</span>
                      <span className={styles['d-value']}>{unit.unit_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>System Identifier</span>
                      <span className={styles['d-value']}>UNIT-ID-{unit.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage History */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Global Usage Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>Historical data for this unit is being compiled across POs and Inventory logs.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>REGISTERED BY</label>
                       <span>{unit.creator?.name || 'System Admin'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>CREATED ON</label>
                       <span>{new Date(unit.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <UnitDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        unit={unit}
        onSave={fetchUnit}
      />
    </div>
  );
};

export default ViewUnit;
